import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError, ROLE_RANK, type AdminRole, type AdminUser } from './api'

interface AuthState {
  user: AdminUser | null
  loading: boolean
  login: (email: string, password: string, totp?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  can: (minimum: AdminRole) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: AdminUser }>('/api/admin/auth/me')
      setUser(user)
    } catch (err) {
      // 401 here is the normal "not signed in" case, not a failure worth surfacing.
      if (!(err instanceof ApiError && err.isUnauthorized)) console.error(err)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const { user } = await api.post<{ user: AdminUser }>('/api/admin/auth/login', { email, password, totp })
    setUser(user)
  }, [])

  const logout = useCallback(async () => {
    await api.post('/api/admin/auth/logout')
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      refresh,
      // Role gates in the UI only hide what the API would refuse anyway — the server is the
      // authority. This exists so operators are not shown buttons that will 403.
      can: (minimum) => (user ? ROLE_RANK[user.role] >= ROLE_RANK[minimum] : false),
    }),
    [user, loading, login, logout, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
